import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { IoChevronDown } from "react-icons/io5";
import { NAV_ITEMS, isNavGroup, type NavGroup, type NavLeaf } from "@/app/nav-config";
import { useSidebarBadges } from "@/app/use-sidebar-badges";
import { useAuth } from "@/features/auth/AuthContext";
import { cn } from "@/lib/cn";

const linkClass = (isActive: boolean, nested = false) =>
  cn(
    "flex items-center gap-3 rounded-md py-2 text-sm font-medium transition-colors",
    nested ? "pl-9 pr-3" : "px-3",
    isActive
      ? "bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300"
      : "text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/50",
  );

function Badge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      aria-label={`${count} new`}
      className="min-w-5 shrink-0 rounded-full bg-brand-600 px-1.5 text-center text-[11px] font-semibold leading-5 text-white"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

function LeafLink({ item, count, nested }: { item: NavLeaf; count: number; nested?: boolean }) {
  return (
    <NavLink to={item.to} end={item.to === "/"} className={({ isActive }) => linkClass(isActive, nested)}>
      {!nested && <item.icon size={18} className="shrink-0" />}
      <span className="flex-1 truncate">{item.label}</span>
      <Badge count={count} />
    </NavLink>
  );
}

function GroupItem({
  group,
  badges,
  isCurrent,
  open,
  onToggle,
}: {
  group: NavGroup;
  badges: Record<string, number>;
  isCurrent: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  // Enquiries and sign-ups live inside groups now. Folded away, their counts
  // would be invisible — so a shut group carries its children's total and new
  // mail still announces itself from the top level.
  const hiddenCount = open ? 0 : group.children.reduce((total, child) => total + (badges[child.to] ?? 0), 0);

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={cn(
          "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          isCurrent
            ? "text-brand-700 dark:text-brand-300"
            : "text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/50",
        )}
      >
        <group.icon size={18} className="shrink-0" />
        <span className="flex-1 truncate text-left">{group.label}</span>
        <Badge count={hiddenCount} />
        <IoChevronDown size={14} className={cn("shrink-0 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="mt-0.5 space-y-0.5">
          {group.children.map((child) => (
            <LeafLink key={child.to} item={child} count={badges[child.to] ?? 0} nested />
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar() {
  const { hasPermission } = useAuth();
  const badges = useSidebarBadges();
  const { pathname } = useLocation();

  // A group survives only if at least one of its children does.
  const visible = NAV_ITEMS.map((entry) => {
    if (!isNavGroup(entry)) return hasPermission(entry.resource, entry.action) ? entry : null;
    const children = entry.children.filter((child) => hasPermission(child.resource, child.action));
    return children.length > 0 ? { ...entry, children } : null;
  }).filter((entry): entry is NonNullable<typeof entry> => entry !== null);

  const currentGroup =
    visible.find(
      (entry) =>
        isNavGroup(entry) &&
        entry.children.some((child) => pathname === child.to || pathname.startsWith(`${child.to}/`)),
    )?.label ?? null;

  /**
   * One group open at a time, following wherever you are.
   *
   * The open group is state rather than derived from the route, so a section
   * can also be opened or shut by hand — but moving to another section resets
   * it, which is what closes the one you have left behind.
   */
  const [openGroup, setOpenGroup] = useState<string | null>(currentGroup);

  useEffect(() => {
    setOpenGroup(currentGroup);
  }, [currentGroup]);

  return (
    // Pinned to the viewport: the menu stays put while the record beside it
    // scrolls, instead of sliding away once a long form runs past its end.
    <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 md:flex">
      <div className="flex h-12 shrink-0 items-center px-6">
        <span className="font-display text-lg font-semibold text-slate-900 dark:text-slate-100">Gallery CMS</span>
      </div>

      {/* Only the list scrolls, and only when it is taller than the screen. */}
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-6">
        {visible.map((entry) =>
          isNavGroup(entry) ? (
            <GroupItem
              key={entry.label}
              group={entry}
              badges={badges}
              isCurrent={currentGroup === entry.label}
              open={openGroup === entry.label}
              // Opening one shuts the other; clicking the open one shuts it.
              onToggle={() => setOpenGroup((current) => (current === entry.label ? null : entry.label))}
            />
          ) : (
            <LeafLink key={entry.to} item={entry} count={badges[entry.to] ?? 0} />
          ),
        )}
      </nav>
    </aside>
  );
}
